import React, { useState } from "react";
import axios from "axios";
import { X, Heart, Check } from "lucide-react";
import PayPalCheckout, { PayDivider, isNativeApp } from "./PayPalCheckout";
import { useCurrency } from "../contexts/CurrencyContext";
import { useAuth } from "../contexts/AuthContext";
import { useI18n } from "../contexts/I18nContext";
import { useNativePayPal } from "../lib/paypalNative";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// One-off unlock for the detailed Ashtakoot match report (₹149 / $3.00).
export default function MatchUnlockModal({ onClose, onSuccess }) {
  const { user, refreshUser } = useAuth();
  const { t } = useI18n();
  const { isUSD, locked, price, priceInr } = useCurrency();
  const [form, setForm] = useState({ name: user?.name || "", email: user?.email || "", phone: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const nativeUsd = isNativeApp() && isUSD;
  const hideInr = isUSD && locked && !nativeUsd;
  const pp = useNativePayPal(async () => { await refreshUser?.(); onSuccess?.(); });

  const pay = async (e) => {
    e.preventDefault();
    if (nativeUsd) return pp.start("match", form);
    setError(""); setLoading(true);
    try {
      const res = await axios.post(`${API}/payments/create-order`,
        { plan: "match", name: form.name, email: form.email, phone: form.phone },
        { withCredentials: true });
      const { order_id, payment_session_id, mock, environment } = res.data;

      if (mock) {
        await axios.post(`${API}/payments/verify/${order_id}`, {}, { withCredentials: true });
        await refreshUser?.();
        onSuccess?.();
        return;
      }
      if (!window.Cashfree) {
        setError(t("match.sdkError"));
        return;
      }
      const cashfree = window.Cashfree({ mode: environment === "production" ? "production" : "sandbox" });
      const result = await cashfree.checkout({ paymentSessionId: payment_session_id, redirectTarget: "_modal" });
      if (result?.error) {
        setError(result.error.message || t("match.payCancelled"));
        return;
      }
      const verify = await axios.post(`${API}/payments/verify/${order_id}`, {}, { withCredentials: true });
      if (verify.data?.status === "success") {
        await refreshUser?.();
        onSuccess?.();
      } else {
        setError(t("match.verifyFailed"));
      }
    } catch (err) {
      setError(err.response?.data?.detail || t("match.payFailed"));
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="match-unlock-modal"
      style={{ position: "fixed", inset: 0, zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(0,0,0,0.85)", backdropFilter: "blur(12px)", padding: "16px" }}>
      <div className="glass-card animate-fadeInUp"
        style={{ maxWidth: "420px", width: "100%", background: "rgba(18,0,33,0.97)", border: "1px solid rgba(255,209,102,0.18)", padding: "28px", position: "relative" }}>
        <button data-testid="match-unlock-close" onClick={onClose}
          style={{ position: "absolute", top: "18px", right: "18px", background: "none", border: "none", cursor: "pointer", color: "rgba(255,255,255,0.4)" }}>
          <X size={18} />
        </button>
        <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "6px" }}>
          <Heart size={18} style={{ color: "#f9a8d4" }} />
          <h2 className="font-cinzel" style={{ color: "#FFD166", fontSize: "1.25rem" }}>{t("match.unlockTitle")}</h2>
        </div>
        <p style={{ color: "rgba(255,255,255,0.55)", fontSize: "0.86rem", marginBottom: "14px" }}>{t("match.unlockDesc")} <strong style={{ color: "#FFD166" }}>{price("match")}</strong></p>
        <div style={{ display: "flex", flexDirection: "column", gap: "6px", marginBottom: "18px" }}>
          {[t("match.feat1"), t("match.feat2"), t("match.feat3"), t("match.feat4")].map(f => (
            <div key={f} style={{ display: "flex", alignItems: "center", gap: "7px", color: "rgba(255,255,255,0.72)", fontSize: "0.84rem" }}>
              <Check size={13} style={{ color: "#86efac", flexShrink: 0 }} /> {f}
            </div>
          ))}
        </div>
        <form onSubmit={pay}>
          <input data-testid="match-pay-name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
            placeholder={t("birth.name")} className="cosmic-input" required style={{ marginBottom: "10px" }} />
          <input data-testid="match-pay-email" type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })}
            placeholder="you@gmail.com" className="cosmic-input" required style={{ marginBottom: "10px" }} />
          <input data-testid="match-pay-phone" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })}
            placeholder="+91 90000 00000" className="cosmic-input" required style={{ marginBottom: "14px" }} />
          {(error || pp.error) && <div data-testid="match-pay-error" style={{ color: "#fca5a5", fontSize: "0.82rem", marginBottom: "12px" }}>{error || pp.error}</div>}
          {pp.pendingOrder && (
            <div data-testid="match-paypal-waiting" style={{ color: "#FFD166", fontSize: "0.8rem", background: "rgba(255,209,102,0.08)", padding: "10px 12px", borderRadius: "8px", marginBottom: "12px", lineHeight: 1.5 }}>
              Complete the PayPal payment in your browser, then return here — your report unlocks automatically.
            </div>
          )}
          <div style={{ display: "flex", flexDirection: (isUSD && !nativeUsd) ? "column-reverse" : "column" }}>
            {!hideInr && (
              <button data-testid="match-pay-btn" type="submit" disabled={loading || pp.starting} className="btn-gold"
                style={{ width: "100%", fontSize: "0.95rem", opacity: (loading || pp.starting) ? 0.6 : 1 }}>
                {loading || pp.starting ? t("match.processing")
                  : nativeUsd ? `${price("match")} — PayPal`
                  : `${priceInr("match")} — ${t("match.payNow")}`}
              </button>
            )}
            {isUSD && !nativeUsd && (
              <>
                <PayPalCheckout
                  plan="match"
                  payer={form}
                  variant="primary"
                  label="Pay"
                  onPaid={async () => { await refreshUser?.(); onSuccess?.(); }}
                  onError={(m) => setError(m)}
                />
                {!locked && <PayDivider text="or pay in ₹ (UPI / card)" />}
              </>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
